import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Copy, ExternalLink, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PageIssue, NearDuplicateDetails } from "@/types/technicalAudit";

interface NearDuplicateCardProps {
	issue: PageIssue;
}

function getSimilarityColor(score: number): { bar: string; text: string } {
	if (score >= 0.9) return { bar: "bg-red-500", text: "text-red-700 dark:text-red-400" };
	if (score >= 0.7) return { bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-400" };
	return { bar: "bg-yellow-500", text: "text-yellow-700 dark:text-yellow-400" };
}

export function NearDuplicateCard({ issue }: NearDuplicateCardProps) {
	const details = issue.issue_details as NearDuplicateDetails;
	const pct = Math.round(details.similarity_score * 100);
	const colors = getSimilarityColor(details.similarity_score);

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Near-Duplicate Content</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						This page is {pct}% similar to another page. Search engines may treat one as duplicate and drop it from results.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Copy className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Similarity Score
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="flex items-center justify-between">
						<span className={cn("text-2xl font-bold", colors.text)}>
							{pct}%
						</span>
					</div>
					<div className="w-full h-3 bg-muted rounded-full overflow-hidden">
						<div
							className={cn("h-full rounded-full transition-all", colors.bar)}
							style={{ width: `${pct}%` }}
						/>
					</div>
				</CardContent>
			</Card>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center">
							<Copy className="w-4 h-4 text-orange-600 dark:text-orange-400" />
						</div>
						Similar Page
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<a
						href={details.similar_url}
						onClick={(e) => openWithCacheBuster(e, details.similar_url)}
						target="_blank"
						rel="noopener noreferrer"
						className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
					>
						<p className="text-sm font-mono break-all text-foreground flex-1">
							{details.similar_url}
						</p>
						<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
					</a>
				</CardContent>
			</Card>
		</div>
	);
}
